/**
 * TargetTableRow — the row component for the cibles table.
 *
 * Contains:
 *   - ColorPicker     — popover color-swatch selector (exclusive to TargetTableRow)
 *   - AliasChip       — alias-count chip with popover list (exclusive to TargetTableRow)
 *   - TargetTableRow  — full row component (view + edit dialog dispatch)
 */
import { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { useTranslation } from 'react-i18next';
import { TARGET_COLOR_PALETTE, type TargetColorToken } from '@traaker/shared';
import {
  type TargetRow, type TagRegistry, type CatConfig,
  tagNamesOf, getFieldValues, COLOR_HEX, GREY, colorHex,
} from './CATEGORIES';
import { TagChips, GenericTargetDialog } from './CatalogueViews';
import {
  EstablishmentAddForm,
  PrecisionMeter,
  EtablissementZoneIcon,
  precisionLevel,
  countryFlag,
} from './EstablishmentForm';
import { PersonneAddForm } from './PersonneForm';

export { TagChips };

// ---------------------------------------------------------------------------
// ColorPicker popover
// ---------------------------------------------------------------------------

export function ColorPicker({
  current, onSelect,
}: { current?: string | null; onSelect: (token: TargetColorToken | null) => void }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        p: 1.25,
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 0.75,
        width: 156,
      }}
    >
      {/* Grey = no color */}
      <Tooltip title={t('cibles.color.default')}>
        <Box
          onClick={() => onSelect(null)}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: GREY,
            cursor: 'pointer',
            outline: !current ? '2px solid #6B7280' : 'none',
            outlineOffset: 2,
            '&:hover': { transform: 'scale(1.2)' },
            transition: 'transform 0.1s',
          }}
        />
      </Tooltip>
      {TARGET_COLOR_PALETTE.map((token) => (
        <Tooltip key={token} title={token}>
          <Box
            onClick={() => onSelect(token)}
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: COLOR_HEX[token],
              cursor: 'pointer',
              outline: current === token ? `2px solid ${COLOR_HEX[token]}` : 'none',
              outlineOffset: 2,
              '&:hover': { transform: 'scale(1.2)' },
              transition: 'transform 0.1s',
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// AliasChip — alias count chip with popover list (établissement)
// ---------------------------------------------------------------------------

/**
 * The aliases are collapsed to a COUNT, not listed inline.
 *
 * An alias is a full établissement name ("Salon Meir", "Dessange Anvers Centre"), so three of
 * them printed next to the real name is three things that all look like the row's identity.
 * The count opens the list, and the list is where they can be removed one at a time — the same
 * reasoning as the kebab: the row shows what the cible IS, the popover shows how it is matched.
 */
export function AliasChip({
  aliases, disabled, onRemove, onEdit,
}: {
  aliases: string[];
  disabled: boolean;
  onRemove: (alias: string) => Promise<void>;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (aliases.length === 0) return null;

  return (
    <>
      <Tooltip title={t('cibles.etablissement.alias_tooltip')}>
        <Box
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setAnchor(e.currentTarget as HTMLElement); }}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'pointer',
            bgcolor: alpha('#000', 0.045), color: 'text.secondary',
            fontSize: '0.7rem', fontWeight: 600, px: 0.75, py: 0.2, borderRadius: 0.5,
            lineHeight: '1.4',
            '&:hover': { bgcolor: alpha('#000', 0.08), color: 'text.primary' },
          }}
        >
          {t('cibles.etablissement.alias_count', { count: aliases.length })}
        </Box>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { boxShadow: '0 4px 16px rgba(0,0,0,.1)', borderRadius: 1.5, minWidth: 230 } }}
      >
        <Box sx={{ py: 1, px: 1.5 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.disabled', mb: 0.75 }}>
            {t('cibles.etablissement.alias_popover_title')}
          </Typography>
          {aliases.map((a) => (
            <Box key={a} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.35 }}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', wordBreak: 'break-word' }}>
                {a}
              </Typography>
              {busy === a ? (
                <CircularProgress size={12} sx={{ color: 'text.disabled' }} />
              ) : (
                <IconButton
                  size="small"
                  disabled={disabled || !!busy}
                  onClick={async () => {
                    setBusy(a);
                    // The popover stays open on failure: `onRemove` reports the error itself
                    // and re-throws, and closing here would take the message off screen with
                    // the alias still listed.
                    try { await onRemove(a); setAnchor(null); } catch { /* reported upstream */ }
                    finally { setBusy(null); }
                  }}
                  sx={{ color: alpha('#000', 0.25), p: 0.25, '&:hover': { color: 'error.main', bgcolor: alpha('#EF4444', 0.07) } }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              )}
            </Box>
          ))}
          <Box
            component="span"
            onClick={() => { setAnchor(null); onEdit(); }}
            sx={{ display: 'inline-block', mt: 0.75, fontSize: '0.75rem', fontWeight: 700, color: '#059669', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('cibles.etablissement.alias_edit_link')}
          </Box>
        </Box>
      </Popover>
    </>
  );
}

// ---------------------------------------------------------------------------
// TargetTableRow
// ---------------------------------------------------------------------------

export function TargetTableRow({
  row, cat, marques, locked, onDelete, onSave, onColorChange, isRoot,
  tagRegistry, workingLang,
}: {
  row: TargetRow;
  cat: CatConfig;
  marques: TargetRow[];
  locked: boolean;
  onDelete: () => void;
  onSave: (updated: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[] }) => Promise<void>;
  onColorChange?: (color: TargetColorToken | null) => Promise<void>;
  isRoot?: boolean;
  tagRegistry: TagRegistry;
  workingLang: string;
}) {
  const { t } = useTranslation();
  /** Tracks in-flight saves from the edit dialog so its buttons show a spinner. */
  const [dialogSaving, setDialogSaving] = useState(false);
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<HTMLElement | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [localColor, setLocalColor] = useState<TargetColorToken | null>(row.color ?? null);
  useEffect(() => { setLocalColor(row.color ?? null); }, [row.color]);

  /**
   * Remove one alias from the établissement/personne popover's `×`.
   * Re-sends EVERY field minus that alias; `tags` absent so removal never clears them.
   */
  const removeAlias = async (alias: string) => {
    const fields = row.fields
      .filter((f) => !(f.name === 'alias' && String(f.value) === alias))
      .map((f) => ({ name: f.name, value: f.value }));
    await onSave({ label: row.label, parentTargetId: row.parentTargetId, fields });
  };

  const hasWarn = (row.warnings?.length ?? 0) > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        // Établissement / personne custom view: flex-start + shared row padding + gap between cols.
        // All other categories: center-align with no gap (cols manage their own px/py).
        alignItems: 'flex-start',
        gap: (cat.id === 'etablissement' || cat.id === 'personne') ? 2.5 : 0,
        py: (cat.id === 'etablissement' || cat.id === 'personne') ? 2 : 0,
        px: (cat.id === 'etablissement' || cat.id === 'personne') ? 3 : 0,
        borderBottom: '1px solid',
        borderColor: alpha('#000', 0.06),
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { bgcolor: alpha('#000', 0.012) },
        transition: 'background 0.1s',
      }}
    >
      {/* Parent marque column */}
      {cat.hasParent && (
        <Box sx={{
          width: 140,
          flexShrink: 0,
          // Établissement / personne custom view: no px/py (outer row handles spacing); 3px top to align chip baseline.
          // Other views: own padding.
          ...((cat.id === 'etablissement' || cat.id === 'personne')
            ? { paddingTop: '3px' }
            : { px: 1.5, py: 0.875 }),
        }}>
          {cat.id === 'etablissement' ? (
            (() => {
              const lbl = marques.find((m) => m.targetId === row.parentTargetId)?.label;
              return lbl ? (
                <Box sx={{ display: 'inline-flex', bgcolor: alpha('#059669', 0.1), color: '#0B6647', fontWeight: 700, fontSize: '0.75rem', letterSpacing: 0.3, px: 1, py: 0.35, borderRadius: 0.75 }}>
                  {lbl}
                </Box>
              ) : null;
            })()
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: cat.color, fontWeight: 500 }}>
              {marques.find((m) => m.targetId === row.parentTargetId)?.label ?? ''}
            </Typography>
          )}
        </Box>
      )}

      {/* Color swatch — marque only (D45) */}
      {cat.id === 'marque' && (
        <Box sx={{ width: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.875 }}>
          <Tooltip title={t('cibles.marque.color_tooltip')}>
            <Box
              onClick={(e) => !locked && setColorAnchor(e.currentTarget as HTMLElement)}
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                // No color selected:
                //   root brand → emerald (brand identity marker)
                //   other brands → grey (neutral, user can pick a color)
                // Color selected → the chosen palette color
                bgcolor: localColor
                  ? colorHex(localColor)
                  : isRoot ? COLOR_HEX.emerald : GREY,
                cursor: locked ? 'default' : 'pointer',
                border: '1.5px solid',
                borderColor: alpha('#000', 0.12),
                '&:hover': locked ? {} : {
                  transform: 'scale(1.25)',
                  borderColor: localColor ? colorHex(localColor) : isRoot ? COLOR_HEX.emerald : GREY,
                },
                transition: 'transform 0.1s, border-color 0.1s',
              }}
            />
          </Tooltip>
          <Popover
            open={!!colorAnchor}
            anchorEl={colorAnchor}
            onClose={() => setColorAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <ColorPicker
              current={localColor}
              onSelect={async (token) => {
                setColorAnchor(null);
                const prev = localColor;
                setLocalColor(token); // optimistic
                if (onColorChange) {
                  try {
                    await onColorChange(token);
                  } catch {
                    setLocalColor(prev); // revert on error
                  }
                }
              }}
            />
          </Popover>
        </Box>
      )}

      {/* Établissement — custom precision-focused view */}
      {cat.id === 'etablissement' && (() => {
        const zoneRaw = row.fields.find((f) => f.name === 'zone')?.value;
        // Support both structured TargetZone objects and raw string values.
        const zone = zoneRaw && typeof zoneRaw === 'object' ? zoneRaw : null;
        // Build the best available label: explicit label → fallback chain → raw string
        const zoneLabel: string = typeof zoneRaw === 'string'
          ? zoneRaw
          : (zone?.label || zone?.address || zone?.cityName || (zone?.regionName ? `${zone.regionName}` : '') || '');
        const zoneCountry: string = zone?.countryCode ?? '';
        const urlVal = row.fields.find((f) => f.name === 'url')?.value;
        const phoneVal = row.fields.find((f) => f.name === 'phone')?.value;
        const hasExplicitName = !!row.fields.find((f) => f.name === 'name')?.value;
        const level = precisionLevel(row);
        const aliases = row.fields.filter((f) => f.name === 'alias').map((f) => String(f.value)).filter(Boolean);
        const tagNames = tagNamesOf(row, tagRegistry);
        return (
          <>
            {/* Établissement name + location — flex:1, no px/py (from outer row) */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {/* Name line */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  {row.label}
                </Typography>
                {!hasExplicitName && (
                  <>
                    <Box sx={{ bgcolor: '#F2F4F1', color: 'text.secondary', fontSize: '0.7rem', px: 0.75, py: 0.2, borderRadius: 0.5, lineHeight: '1.4' }}>
                      {t('cibles.etablissement.auto_name')}
                    </Box>
                    <Box
                      component="span"
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); setEditDialogOpen(true); }}
                      sx={{ fontSize: '0.775rem', fontWeight: 700, color: '#059669', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {t('cibles.etablissement.nommer')}
                    </Box>
                  </>
                )}
                <AliasChip
                  aliases={aliases}
                  disabled={locked}
                  onRemove={removeAlias}
                  onEdit={() => setEditDialogOpen(true)}
                />
              </Box>
              {/* Location line — shown whenever any zone data exists */}
              {(zoneLabel || zoneCountry) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', width: '100%', minWidth: 0, overflow: 'hidden' }}>
                  <EtablissementZoneIcon level={level} />
                  {zoneLabel && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '13px',
                        color: '#3D4740',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flexShrink: 1,
                        minWidth: 0,
                      }}
                    >
                      {zoneLabel}
                    </Box>
                  )}
                  {zoneCountry && (
                    <>
                      <Box sx={{ width: '1px', height: '10px', bgcolor: '#DCE0DA', flexShrink: 0 }} />
                      <Box component="span" sx={{ fontSize: '1rem', lineHeight: 1, flexShrink: 0 }}>
                        {countryFlag(zoneCountry)}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              {/* Tag line — its own row, not appended to the name line: a tag is a
                  classification the customer invented, and sitting it next to the name would
                  read as part of the établissement's identity (which is what the alias chip
                  IS). Wraps rather than truncating the list — 20 are allowed. */}
              {tagNames.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <TagChips names={tagNames} />
                </Box>
              )}
            </Box>

            {/* Precision column — 300px, no px/py (from outer row) */}
            <Box sx={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0.375 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
                <PrecisionMeter level={level} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  {t(`cibles.precision.level${level}`)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>{level}/3</Typography>
              </Box>
              {level < 3 ? (
                <>
                  <Box
                    component="span"
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); setEditDialogOpen(true); }}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.775rem', fontWeight: 600, color: '#059669', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {level === 1 ? t('cibles.precision.next_address') : t('cibles.precision.next_place')}
                    {' →'}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#8A928D', lineHeight: 1.3 }}>
                    {level === 1 ? t('cibles.precision.next_address_hint') : t('cibles.precision.next_place_hint')}
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: alpha('#059669', 0.09), color: '#0B6647', px: 0.75, py: 0.3, borderRadius: 0.75, alignSelf: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>✓ {t('cibles.precision.linked')}</Typography>
                </Box>
              )}
            </Box>

            {/* Contact icons — 172px, no px/py (from outer row) */}
            <Box sx={{ width: 172, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0.625 }}>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                {urlVal ? (
                  <Tooltip title={String(urlVal)}>
                    <Box
                      onClick={() => { const u = String(urlVal); window.open(u.startsWith('http') ? u : `https://${u}`, '_blank', 'noopener,noreferrer'); }}
                      sx={{ width: 34, height: 34, borderRadius: 1.25, bgcolor: alpha('#059669', 0.09), color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', '&:hover': { bgcolor: alpha('#059669', 0.18) } }}
                    >
                      <PublicIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('cibles.col.etablissement.url')}>
                    <Box onClick={() => setEditDialogOpen(true)} sx={{ width: 34, height: 34, borderRadius: 1.25, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'text.disabled', flexShrink: 0, '&:hover': { borderColor: 'text.secondary', color: 'text.secondary' } }}>
                      <AddIcon sx={{ fontSize: 13 }} />
                    </Box>
                  </Tooltip>
                )}
                {phoneVal ? (
                  <Tooltip title={String(phoneVal)}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.25, bgcolor: alpha('#059669', 0.09), color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('cibles.col.etablissement.phone')}>
                    <Box onClick={() => setEditDialogOpen(true)} sx={{ width: 34, height: 34, borderRadius: 1.25, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'text.disabled', flexShrink: 0, '&:hover': { borderColor: 'text.secondary', color: 'text.secondary' } }}>
                      <AddIcon sx={{ fontSize: 13 }} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              {level === 3 && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                  {t('cibles.etablissement.repris_fiche')}
                </Typography>
              )}
            </Box>
          </>
        );
      })()}

      {/* Data columns — display only (edit opens a dialog for all categories) */}
      {cat.id !== 'etablissement' && cat.id !== 'personne' && cat.cols.map((col) => {
        const multiVals = col.multi
          ? row.fields.filter((f) => f.name === col.field).map((f) => String(f.value)).filter(Boolean)
          : [];
        return (
          <Box key={col.field} sx={{ width: col.width, flexShrink: 0, px: 1.5, py: 0.875 }}>
            {col.multi ? (
              multiVals.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.375, alignItems: 'center' }}>
                  {multiVals.slice(0, 5).map((v) => (
                    <Box
                      key={v}
                      sx={{
                        fontSize: '0.72rem', fontWeight: 500,
                        bgcolor: alpha(cat.color, 0.09), color: cat.color,
                        px: 0.75, py: 0.15, borderRadius: 0.625, lineHeight: 1.6,
                        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {v}
                    </Box>
                  ))}
                  {multiVals.length > 5 && (
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', lineHeight: 1.6 }}>
                      +{multiVals.length - 5}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8125rem' }}>
                  {col.optional ? t('cibles.optional_value') : ''}
                </Typography>
              )
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  color: getFieldValues(row, col.field) ? 'text.primary' : 'text.disabled',
                  fontStyle: getFieldValues(row, col.field) ? 'normal' : 'italic',
                }}
              >
                {getFieldValues(row, col.field) || (
                  <Typography component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8125rem' }}>
                    {col.optional ? t('cibles.optional_value') : ''}
                  </Typography>
                )}
                {/* Root brand badge — name column only */}
                {isRoot && col.field === 'name' && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.75, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.4,
                      textTransform: 'uppercase', color: cat.color, opacity: 0.7,
                      bgcolor: alpha(cat.color, 0.1), px: 0.6, py: 0.15, borderRadius: 0.75,
                      verticalAlign: 'middle',
                    }}
                  >
                    {t('cibles.marque.default_badge')}
                  </Typography>
                )}
              </Typography>
            )}
          </Box>
        );
      })}

      {/* Personne — custom compact view */}
      {cat.id === 'personne' && (() => {
        const aliases = row.fields.filter((f) => f.name === 'alias').map((f) => String(f.value)).filter(Boolean);
        const tagNames = tagNamesOf(row, tagRegistry);
        return (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Name line */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {row.label}
              </Typography>
              {aliases.length > 0 && (
                <Box
                  component="span"
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); setEditDialogOpen(true); }}
                  sx={{
                    fontSize: '0.775rem', fontWeight: 600, color: cat.color, cursor: 'pointer',
                    borderBottom: `1px dotted ${alpha(cat.color, 0.5)}`,
                    '&:hover': { borderBottomStyle: 'solid' },
                  }}
                >
                  {aliases.length}{' '}{t('cibles.personne.noms_count', { count: aliases.length })}
                </Box>
              )}
            </Box>
            {tagNames.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <TagChips names={tagNames} />
              </Box>
            )}
          </Box>
        );
      })()}

      {/* Warning indicator */}
      {hasWarn && (
        <Tooltip title={row.warnings!.map((w) => w.message || w.kind).join(' · ')}>
          <Box sx={{ width: 24, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'warning.main' }}>
            <Typography sx={{ fontSize: 14, lineHeight: 1 }}>⚠</Typography>
          </Box>
        </Tooltip>
      )}

      {/* Actions — kebab + dialog for all categories */}
      <Box sx={{
        ml: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0,
        ...((cat.id === 'etablissement' || cat.id === 'personne') ? { width: 36 } : { px: 1, py: 0.75 }),
      }}>
        <IconButton size="small" onClick={(e) => setKebabAnchor(e.currentTarget as HTMLElement)} disabled={locked}
          sx={{ color: alpha('#000', 0.25), '&:hover': { bgcolor: alpha('#000', 0.06), color: 'text.secondary' } }}>
          <MoreHorizIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Popover
          open={!!kebabAnchor}
          anchorEl={kebabAnchor}
          onClose={() => setKebabAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { boxShadow: '0 4px 16px rgba(0,0,0,.1)', borderRadius: 1.5, minWidth: 140 } }}
        >
          <Box sx={{ py: 0.5 }}>
            <MenuItem onClick={() => { setKebabAnchor(null); setEditDialogOpen(true); }} sx={{ fontSize: '0.8125rem', gap: 1, py: 0.75 }}>
              <EditOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              {t('cibles.actions.edit')}
            </MenuItem>
            <MenuItem onClick={() => { setKebabAnchor(null); onDelete(); }} sx={{ fontSize: '0.8125rem', gap: 1, py: 0.75, color: 'error.main' }}>
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              {t('cibles.actions.delete')}
            </MenuItem>
          </Box>
        </Popover>
        {/* Edit dialog — category-specific or generic */}
        {cat.id === 'etablissement' ? (
          <EstablishmentAddForm
            open={editDialogOpen}
            mode="edit"
            initialRow={row}
            marques={marques}
            onAdd={async (data) => { setDialogSaving(true); try { await onSave(data); setEditDialogOpen(false); } finally { setDialogSaving(false); } }}
            onClose={() => setEditDialogOpen(false)}
            saving={dialogSaving}
            zoneState={{ query: '', suggestions: [], attribution: '', selected: null, onQueryChange: () => {}, onSelect: () => {} }}
            tagRegistry={tagRegistry}
          />
        ) : cat.id === 'personne' ? (
          <PersonneAddForm
            open={editDialogOpen}
            mode="edit"
            initialRow={row}
            marques={marques}
            onAdd={async (data) => { setDialogSaving(true); try { await onSave(data); setEditDialogOpen(false); } finally { setDialogSaving(false); } }}
            onClose={() => setEditDialogOpen(false)}
            saving={dialogSaving}
            tagRegistry={tagRegistry}
            workingLang={workingLang}
          />
        ) : (
          <GenericTargetDialog
            open={editDialogOpen}
            mode="edit"
            cat={cat}
            initialRow={row}
            marques={marques}
            onSave={async (data) => { setDialogSaving(true); try { await onSave(data); setEditDialogOpen(false); } finally { setDialogSaving(false); } }}
            onClose={() => setEditDialogOpen(false)}
            saving={dialogSaving}
            tagRegistry={tagRegistry}
          />
        )}
      </Box>
    </Box>
  );
}
